import { useState, useEffect, useCallback } from "react";
import techniciansService from "../../../api/techniciansService";
import userService from "../../../api/userService";
import { sortTechnicians } from "../../../utils/sortUtils";

/**
 * Hook personalizado para manejar la lógica de técnicos
 */
export const useTechnicians = () => {
  const [technicians, setTechnicians] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar técnicos
  const loadTechnicians = useCallback(async () => {
    try {
      setLoading(true);
      const res = await techniciansService.getAll();
      const techniciansData = res.data.data || [];
      setTechnicians(sortTechnicians(techniciansData));
      return techniciansData;
    } catch (err) {
      console.error("Error loading technicians", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar usuarios
  const loadUsers = useCallback(async () => {
    try {
      const res = await userService.getAllUsers();
      setUsers(Array.isArray(res.data.data) ? res.data.data : []);
      return res.data.data || [];
    } catch (err) {
      console.error("Error loading users", err);
      setUsers([]);
      throw err;
    }
  }, []);

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([loadTechnicians(), loadUsers()]);
    };
    loadInitialData();
  }, [loadTechnicians, loadUsers]);

  // Crear técnico
  const createTechnician = useCallback(async (data) => {
    try {
      const clean = {};
      for (const key in data) {
        if (data[key] !== "" && data[key] !== undefined) {
          clean[key] = data[key];
        }
      }
      await techniciansService.create(clean);
      await loadTechnicians();
      return true;
    } catch (err) {
      console.error("Create technician failed", err);
      throw err;
    }
  }, [loadTechnicians]);

  // Actualizar técnico
  const updateTechnician = useCallback(async (id, data) => {
    try {
      const allowed = [
        "name",
        "phone",
        "assignedVehicle",
        "status",
        "user",
        "zone",
      ];
      const clean = {};

      allowed.forEach((k) => {
        if (data[k] !== "" && data[k] !== undefined) {
          clean[k] = data[k];
        }
      });

      if (data.user === "") {
        clean.user = null;
      }

      await techniciansService.update(id, clean);
      await loadTechnicians();
      return true;
    } catch (err) {
      console.error("Update technician failed", err);
      throw err;
    }
  }, [loadTechnicians]);

  // Eliminar técnico
  const deleteTechnician = useCallback(async (id) => {
    try {
      await techniciansService.delete(id);
      await loadTechnicians();
      return true;
    } catch (err) {
      console.error("Delete technician failed", err);
      throw err;
    }
  }, [loadTechnicians]);

  return {
    technicians,
    users,
    loading,
    loadTechnicians,
    createTechnician,
    updateTechnician,
    deleteTechnician,
  };
};

